import React, { useEffect, useRef, useState } from "react";
import {
  MessageCircle,
  HelpCircle,
  UserCheck,
  TrendingUp,
  Sparkles,
} from "lucide-react";

const features = [
  {
    icon: MessageCircle,
    title: "Engage in Real Time",
    desc: "Start conversations the very moment visitors land on your website.",
    step: "01",
    progress: 25,
    iconBg: "bg-gradient-to-br from-blue-500 to-blue-600",
    accent: "text-blue-600",
    dot: "bg-blue-500",
    glow: "bg-blue-500",
    bar: "bg-gradient-to-r from-blue-500 to-cyan-500",
    tint: "from-blue-50 via-white to-white",
  },
  {
    icon: HelpCircle,
    title: "Answer Questions",
    desc: "Provide instant, helpful answers and build lasting trust with visitors.",
    step: "02",
    progress: 50,
    iconBg: "bg-gradient-to-br from-emerald-500 to-emerald-600",
    accent: "text-emerald-600",
    dot: "bg-emerald-500",
    glow: "bg-emerald-500",
    bar: "bg-gradient-to-r from-emerald-500 to-teal-500",
    tint: "from-emerald-50 via-white to-white",
  },
  {
    icon: UserCheck,
    title: "Capture Contact Details",
    desc: "Collect qualified leads before your visitors ever leave the site.",
    step: "03",
    progress: 75,
    iconBg: "bg-gradient-to-br from-cyan-500 to-cyan-600",
    accent: "text-cyan-600",
    dot: "bg-cyan-500",
    glow: "bg-cyan-500",
    bar: "bg-gradient-to-r from-cyan-500 to-sky-500",
    tint: "from-cyan-50 via-white to-white",
  },
  {
    icon: TrendingUp,
    title: "Increase Conversions",
    desc: "Turn curious visitors into paying, qualified customers every day.",
    step: "04",
    progress: 100,
    iconBg: "bg-gradient-to-br from-orange-500 to-orange-600",
    accent: "text-orange-600",
    dot: "bg-orange-500",
    glow: "bg-orange-500",
    bar: "bg-gradient-to-r from-orange-500 to-amber-500",
    tint: "from-orange-50 via-white to-white",
  },
];

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [options]);
  return [ref, inView];
};

const FeatureCard = ({ f, index, inView }) => {
  const Icon = f.icon;
  return (
    <div
      className={`h-full transition-all duration-700 ease-out ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${index * 140}ms` }}
    >
      <div className="h-full">
        <div className="group relative h-full flex flex-col rounded-3xl bg-white border border-slate-200/70 p-6 sm:p-8 overflow-hidden shadow-lg shadow-slate-200/60 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
          {/* Static tint background */}
          <div
            className={`absolute inset-0 bg-gradient-to-br ${f.tint} opacity-60`}
          />

          {/* Static corner glow */}
          <div
            className={`absolute -top-16 -right-16 w-40 h-40 rounded-full ${f.glow} blur-3xl opacity-30`}
          />

          {/* Content */}
          <div className="relative flex flex-col flex-1">
            {/* Top row: icon + step */}
            <div className="flex items-start justify-between mb-6">
              {/* Icon */}
              <div className="relative">
                <div
                  className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl ${f.iconBg} flex items-center justify-center shadow-xl`}
                >
                  <Icon
                    className="w-8 h-8 sm:w-10 sm:h-10 text-white"
                    strokeWidth={2.2}
                  />
                  {/* Static dot */}
                  <span
                    className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full ${f.dot} ring-2 ring-white`}
                  />
                </div>
              </div>

              {/* Step number */}
              <div className="flex flex-col items-end">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Step
                </span>
                <span
                  className={`text-3xl sm:text-4xl font-black leading-none ${f.accent} opacity-80`}
                >
                  {f.step}
                </span>
              </div>
            </div>

            {/* Title */}
            <h4 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight min-h-[3.25rem] sm:min-h-[3.5rem] flex items-start">
              {f.title}
            </h4>
            <p className="mt-2 text-base sm:text-[17px] text-slate-800 font-medium leading-relaxed flex-1">
              {f.desc}
            </p>

            {/* Progressive static bar */}
            <div className="mt-6">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full ${f.bar} transition-[width] duration-700 ease-out`}
                  style={{ width: inView ? `${f.progress}%` : "0%" }}
                />
              </div>
              <div className="mt-1.5 flex justify-end">
                <span className={`text-[11px] font-bold ${f.accent}`}>
                  {f.progress}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Features = () => {
  const [ref, inView] = useInView();

  return (
    <section
      id="features"
      ref={ref}
      className="pt-8 sm:pt-12 pb-12 sm:pb-16 relative"
    >
      {/* Static ambient background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-16 left-1/4 w-72 h-72 rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute -bottom-16 right-1/4 w-80 h-80 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-cyan-100/40 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={`text-center mb-10 sm:mb-14 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"
          }`}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/80 backdrop-blur px-5 py-2 text-sm sm:text-base font-bold uppercase tracking-wider text-blue-700 shadow-sm">
            <Sparkles className="w-5 h-5" />
            How it works
          </span>
          <h2 className="mt-5 sm:mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
            A conversation that{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-blue-600">converts</span>
              <span
                aria-hidden
                className="absolute left-0 right-0 bottom-1 h-4 sm:h-5 bg-blue-200/70 -z-0 rounded-sm"
              />
            </span>
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-7 items-stretch">
          {features.map((f, i) => (
            <FeatureCard key={f.title} f={f} index={i} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
